<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import CookieBanner from '@/components/CookieBanner.vue'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { initAnalytics } from '@/utils/analytics'

const route = useRoute()
const router = useRouter()

const isDarkTheme = computed(() => {
  return ['/about', '/broadcast'].includes(route.path)
})

const isLightBackground = ref(true)

function checkBackgroundColor() {
  const elementUnderNav = document.querySelector('.page-container > *:first-child')
  if (!elementUnderNav) return

  const bgcolor = window.getComputedStyle(elementUnderNav).backgroundColor

  const color = bgcolor.match(/\d+/g)
  if (!color) return

  const brightness =
    (parseInt(color[0] ?? '0') * 299 +
      parseInt(color[1] ?? '0') * 587 +
      parseInt(color[2] ?? '0') * 114) /
    1000
  isLightBackground.value = brightness > 128
}

onMounted(() => {
  checkBackgroundColor()
  window.addEventListener('scroll', checkBackgroundColor)
  initAnalytics(router)
})

onUnmounted(() => {
  window.removeEventListener('scroll', checkBackgroundColor)
})

const updateThemeClass = (isDark) => {
  document.documentElement.classList.toggle('dark-theme', isDark)
  document.body.classList.toggle('dark-theme', isDark)
}

watch(
  () => isDarkTheme.value,
  (newValue) => {
    updateThemeClass(newValue)
  },
  { immediate: true },
)
</script>

<template>
  <div class="app-layout" :class="{ 'dark-theme': isDarkTheme }">
    <Navbar :is-dark-theme="isDarkTheme" />
    <main class="main-content">
      <RouterView v-slot="{ Component }">
        <Transition name="page-fade" appear>
          <component :is="Component" :key="route.fullPath" />
        </Transition>
      </RouterView>
    </main>
    <Footer />
    <CookieBanner />
  </div>
</template>

<style>
@import '@/assets/main.css';

html,
body {
  overscroll-behavior: none;
  background-color: var(--color-bg-primary);
  transition: background-color 0.3s ease;
}

::-webkit-scrollbar {
  width: 0.375rem;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 0.188rem;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0.188rem;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

html.dark-theme::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

html.dark-theme::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.5);
  border: 0.063rem solid rgba(255, 255, 255, 0.1);
}

html.dark-theme::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.7);
}

* {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.05);
}

html.dark-theme * {
  scrollbar-color: rgba(255, 255, 255, 0.5) rgba(255, 255, 255, 0.1);
}

html.dark-theme,
body.dark-theme {
  background-color: var(--color-bg-dark);
}

.app-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overscroll-behavior: none;
}

.app-layout.dark-theme {
  background-color: var(--color-bg-dark);
}

.main-content {
  flex: 1;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition:
    opacity 0.34s cubic-bezier(0.22, 0.61, 0.36, 1),
    transform 0.34s cubic-bezier(0.22, 0.61, 0.36, 1),
    filter 0.34s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
  filter: blur(1px);
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}

.popup-fade-enter-active .modal-content,
.popup-fade-leave-active .modal-content,
.popup-fade-enter-active .case-popup,
.popup-fade-leave-active .case-popup {
  transition:
    opacity 0.3s ease,
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

.popup-fade-enter-from .modal-content,
.popup-fade-leave-to .modal-content,
.popup-fade-enter-from .case-popup,
.popup-fade-leave-to .case-popup {
  opacity: 0;
  transform: translateY(0.75rem) scale(0.985);
}
</style>
